/**
 * Tela de login — refeita para visual à altura do mockup.
 *
 * 2 colunas em desktop: brand panel (gradient + tagline) + form card.
 * Em mobile, brand colapsa pra header sutil. POST /api/auth/login com
 * `credentials: include` — backend devolve cookies HttpOnly.
 *
 * `?reset=ok` (vindo do /reset-password, PSHWO-67053) mostra o aviso de
 * senha redefinida acima do formulário.
 */

import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { AuthLayout } from "@/components/AuthLayout";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Label } from "@/components/ui/Label";

const schema = z.object({
  email: z.string().min(1, "Email obrigatório").email("Email inválido"),
  password: z.string().min(1, "Senha obrigatória"),
});

type FormData = z.infer<typeof schema>;

/** Search params aceitos pela rota /login. */
export interface LoginSearch {
  reset?: "ok";
}

/** `validateSearch` da rota /login — só aceita valores conhecidos. */
export function validateLoginSearch(raw: Record<string, unknown>): LoginSearch {
  return raw.reset === "ok" ? { reset: "ok" } : {};
}

export function LoginPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const search = useSearch({ strict: false }) as LoginSearch;
  const [serverError, setServerError] = useState<string | null>(null);
  const emailInputRef = useRef<HTMLInputElement | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    mode: "onSubmit",
  });

  const { ref: emailRef, ...emailProps } = register("email");
  useEffect(() => {
    emailInputRef.current?.focus();
  }, []);

  async function onSubmit(data: FormData) {
    setServerError(null);
    let res: Response;
    try {
      res = await fetch("/api/auth/login", {
        method: "POST",
        credentials: "include",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(data),
      });
    } catch {
      setServerError("Não foi possível conectar. Tente novamente.");
      return;
    }
    if (!res.ok) {
      const body = (await res.json().catch(() => ({}))) as { detail?: string };
      setServerError(body.detail ?? "Erro ao entrar. Tente novamente.");
      return;
    }
    navigate({ to: "/" });
  }

  return (
    <AuthLayout>
      <header className="mb-8">
        <div className="mb-1.5 font-mono text-[10px] uppercase tracking-[0.16em] text-[var(--color-muted-foreground)]">
          Acesso ao painel
        </div>
        <h2 className="text-[26px] font-semibold tracking-[-0.01em] text-[var(--color-foreground)]">
          Bem-vindo de volta
        </h2>
        <p className="mt-2 text-[14px] text-[var(--color-muted-foreground)]">
          Entre com seu email e senha para continuar.
        </p>
      </header>

      {search.reset === "ok" ? (
        <div
          role="status"
          className="mb-4 rounded-md border border-[var(--color-success)] bg-[var(--color-success)]/10 px-3 py-2 text-sm text-[var(--color-success)]"
        >
          {t("auth.resetPassword.successBanner")}
        </div>
      ) : null}

      <form
        onSubmit={handleSubmit(onSubmit)}
        className="flex flex-col gap-4"
        noValidate
      >
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            placeholder="voce@empresa.com.br"
            autoComplete="username"
            aria-invalid={errors.email ? true : undefined}
            {...emailProps}
            ref={(el) => {
              emailRef(el);
              emailInputRef.current = el;
            }}
          />
          {errors.email ? (
            <span
              role="alert"
              className="text-xs text-[var(--color-destructive)]"
            >
              {errors.email.message}
            </span>
          ) : null}
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="password">Senha</Label>
          <Input
            id="password"
            type="password"
            placeholder="••••••••"
            autoComplete="current-password"
            aria-invalid={errors.password ? true : undefined}
            {...register("password")}
          />
          {errors.password ? (
            <span
              role="alert"
              className="text-xs text-[var(--color-destructive)]"
            >
              {errors.password.message}
            </span>
          ) : null}
        </div>

        {serverError ? (
          <div
            role="alert"
            className="rounded-md border border-[var(--color-destructive)] bg-[var(--color-destructive)]/10 px-3 py-2 text-sm text-[var(--color-destructive)]"
          >
            {serverError}
          </div>
        ) : null}

        <Button
          type="submit"
          variant="primary"
          size="lg"
          disabled={isSubmitting}
          className="mt-2 w-full"
        >
          {isSubmitting ? "Entrando…" : "Entrar →"}
        </Button>
      </form>

      <p className="mt-6 text-center font-mono text-[11px] uppercase tracking-[0.1em] text-[var(--color-muted-foreground)]">
        Acesso restrito · Suporte:{" "}
        <a
          href="mailto:suporte@priceshow.tv"
          className="text-[var(--color-foreground)] hover:text-[var(--color-primary)]"
        >
          suporte@priceshow.tv
        </a>
      </p>
    </AuthLayout>
  );
}
